import React from 'react'
import { Box, Button, Chip, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material'
import type { ThemeAIScreeningRecordRow } from '../../types/gotypes'
import DisabledReason from '../system/DisabledReason'
import { junkCategoryLabel } from '../../utils/themeAIJunk'

/** What every flagged-answer list shows: the answer, its id and the Keep switch. */
export interface FlaggedAnswerRow {
  record_id: string
  interview_id: string
  source_text: string
  kept: boolean
}

export interface JunkAnswerListProps<T extends FlaggedAnswerRow = ThemeAIScreeningRecordRow> {
  records: T[]
  /** The tags under each answer. Defaults to the junk check's: why, and rule or model. */
  renderTags?: (record: T) => React.ReactNode
  total: number
  offset: number
  pageSize: number
  loading: boolean
  /** Why Keep is closed to this person (they cannot change the task), or undefined. */
  writeBlockedReason?: string
  onSetKept: (recordId: string, kept: boolean) => void
  onPageChange: (offset: number) => void
}

const junkTags = (record: ThemeAIScreeningRecordRow) => (
  <>
    <Chip size="small" variant="outlined" label={junkCategoryLabel(record.category)} />
    <Chip size="small" variant="outlined" label={record.source === 'rule' ? 'Rule' : 'Model'} />
    <Typography variant="caption" color="text.secondary">{record.reason}</Typography>
  </>
)

// The answers the check flagged, each with why. Keep overrides the decision
// for that one answer; it stays in the run whatever the user chooses below.
// The personal data review (#494) lists its answers with this too, with its
// own tags.
function JunkAnswerList<T extends FlaggedAnswerRow = ThemeAIScreeningRecordRow>({
  records,
  renderTags,
  total,
  offset,
  pageSize,
  loading,
  writeBlockedReason,
  onSetKept,
  onPageChange,
}: JunkAnswerListProps<T>) {
  const first = total === 0 ? 0 : offset + 1
  const last = Math.min(offset + records.length, total)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 32 }}>
        <Typography variant="subtitle2">Flagged answers</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
          {loading && records.length === 0 ? 'Loading…' : `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`}
        </Typography>
        {loading && <CircularProgress size={14} />}
      </Box>

      {!loading && records.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No flagged answers here.
        </Typography>
      ) : (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, opacity: loading ? 0.6 : 1, transition: 'opacity 150ms ease-out' }}>
          {records.map((record) => (
            <Box
              component="li"
              key={record.record_id}
              sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 2, rowGap: 0.5, alignItems: 'start' }}
            >
              <Typography variant="body2" sx={{ maxWidth: '75ch', lineHeight: 1.6, overflowWrap: 'anywhere', color: record.source_text.trim() ? undefined : 'text.disabled' }}>
                {record.source_text.trim() ? record.source_text : '(empty)'}
              </Typography>
              <DisabledReason reason={writeBlockedReason}>
                <FormControlLabel
                  label="Keep"
                  labelPlacement="start"
                  sx={{ m: 0, gap: 0.5 }}
                  control={
                    <Switch
                      size="small"
                      color="info"
                      checked={record.kept}
                      onChange={(event) => onSetKept(record.record_id, event.target.checked)}
                      inputProps={{ 'aria-label': `Keep answer ${record.interview_id}` }}
                    />
                  }
                />
              </DisabledReason>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                {renderTags ? renderTags(record) : junkTags(record as unknown as ThemeAIScreeningRecordRow)}
              </Box>
              <Typography variant="caption" color="text.disabled">{record.interview_id}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {total > pageSize && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button size="small" color="info" disabled={offset === 0 || loading} onClick={() => onPageChange(Math.max(0, offset - pageSize))}>
            Previous
          </Button>
          <Button size="small" color="info" disabled={last >= total || loading} onClick={() => onPageChange(offset + pageSize)}>
            Next
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default JunkAnswerList
